import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  addDays,
  format,
  isValid,
  parse,
  parseISO,
  startOfDay,
  subDays,
} from "date-fns";

import { Line } from "react-chartjs-2";

import {
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
} from "chart.js";

import zoomPlugin from "chartjs-plugin-zoom";
import "chartjs-adapter-date-fns";
import InitContext from "../pages/InitContext";
import useMmcService from "../../services/MmcService";
import ErrorMessage from "../errorMessage/ErrorMessage";
import Spinner from "../spinner/Spinner";
import Select from "react-select";
import {Consts} from "../pages/consts";
import useDialogDrag from "../../hooks/useDialogDrag";

ChartJS.register(
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  zoomPlugin,
);

const LEGEND_MAX_VISIBLE_ITEMS = 5;
const LEGEND_SCROLL_HEIGHT_PX = 120;

const VALUE_PALETTE = [
  "#1976d2",
  "#00897b",
  "#f4511e",
  "#8e24aa",
  "#2e7d32",
  "#ef6c00",
  "#5e35b1",
  "#00838f",
  "#c62828",
  "#3949ab",
  "#6d4c41",
  "#7cb342",
  "#00acc1",
  "#ad1457",
  "#546e7a",
  "#43a047",
  "#1e88e5",
  "#fb8c00",
  "#7e57c2",
  "#039be5",
];

const MEDICATION_PALETTE = [
  "#1565c0",
  "#2e7d32",
  "#d81b60",
  "#6a1b9a",
  "#ef6c00",
  "#00838f",
  "#c62828",
  "#455a64",
  "#43a047",
  "#5e35b1",
  "#f9a825",
  "#00897b",
  "#3949ab",
  "#7cb342",
  "#039be5",
  "#8d6e63",
  "#e53935",
  "#1e88e5",
  "#00acc1",
  "#7e57c2",
];

const stableColorForKey = (key, palette) => {
  const resolvedPalette = palette?.length ? palette : VALUE_PALETTE;

  if (!key) {
    return resolvedPalette[0];
  }

  let hash = 0;

  for (let i = 0; i < key.length; i += 1) {
    hash = (hash << 5) - hash + key.charCodeAt(i);
    hash |= 0;
  }

  return resolvedPalette[Math.abs(hash) % resolvedPalette.length];
};

const parseDate = (dateValue) => {
  if (!dateValue) {
    return null;
  }

  if (dateValue instanceof Date) {
    return isValid(dateValue) ? new Date(dateValue) : null;
  }

  if (typeof dateValue !== "string") {
    return null;
  }

  const candidate = dateValue.trim();

  if (!candidate) {
    return null;
  }

  const parsedIso = parseISO(candidate);

  if (isValid(parsedIso)) {
    return parsedIso;
  }

  const formats = [
    "dd/MM/yyyy HH:mm",
    "dd/MM/yyyy",
    "dd.MM.yyyy HH:mm",
    "dd.MM.yyyy",
    "yyyy-MM-dd HH:mm",
    "yyyy-MM-dd",
    "yyyy-MM-dd'T'HH:mm:ss",
    "yyyy-MM-dd'T'HH:mm:ss.SSSX",
  ];

  for (let i = 0; i < formats.length; i += 1) {
    const parsed = parse(candidate, formats[i], new Date());

    if (isValid(parsed)) {
      return parsed;
    }
  }

  return null;
};

const formatTooltipDate = (date) => {
  const parsed = parseDate(date);
  return parsed ? format(parsed, "dd/MM/yyyy") : "-";
};

const normalizeChartPoint = (point) => {
  const parsedX = parseDate(point?.x);
  const numericY = typeof point?.y === Consts.BOOLEAN ? (point?.y ? 1 : 0) : Number(point?.y);

  if (!parsedX || !Number.isFinite(numericY)) {
    return null;
  }

  return {
    ...point,
    x: parsedX,
    y: numericY,
  };
};

const sortPointsChronologically = (points = []) => {
  return [...points].sort((a, b) => {
    const timeA = a?.x instanceof Date ? a.x.getTime() : Number.NaN;
    const timeB = b?.x instanceof Date ? b.x.getTime() : Number.NaN;

    if (!Number.isFinite(timeA) && !Number.isFinite(timeB)) {
      return 0;
    }

    if (!Number.isFinite(timeA)) {
      return 1;
    }

    if (!Number.isFinite(timeB)) {
      return -1;
    }

    return timeA - timeB;
  });
};

const normalizeSourceKey = (value) => {
  if (!value || typeof value !== "string") {
    return "";
  }

  return value.trim().toLowerCase().replace(/[-\s]+/g, "_");
};

const generateIntakePoints = (medication, intervalStart, intervalEnd) => {
  const startDate = parseDate(medication?.startDate);

  if (!startDate || !intervalStart || !intervalEnd) {
    return [];
  }

  const intakes = medication?.intakes?.map((intake) => {
    const [hours, minutes] = intake?.intakeTime?.split(":")?.map(Number);
    return { ...intake, hours, minutes };
  })?.filter((intake) => Number.isFinite(intake?.hours) && Number.isFinite(intake?.minutes));

  let currentDate = startOfDay(intervalStart);
  const intervalEndDay = startOfDay(intervalEnd);
  const points = [];

  const normalizedStartDay = startOfDay(startDate);

  while (currentDate <= intervalEndDay) {
    if (currentDate >= normalizedStartDay) {
      intakes?.forEach((intake) => {
        const intakeDate = new Date(currentDate);
        intakeDate?.setHours(intake?.hours, intake?.minutes);

        points?.push({
          x: intakeDate,
          y: 0.1,
          value: intake?.dosage,
          unit: intake?.dosageUnit,
          label: medication?.productName,
          productName: medication?.productName,
          connectPoints: false,
          highlighted: true,
        });
      });
    }

    currentDate = addDays(currentDate, 1);
  }

  return points;
};

const transformMedicationsToDataSets = (
  medications,
  intervalStart,
  intervalEnd,
) => {
  if (!medications || !intervalStart || !intervalEnd) {
    return [];
  }

  const points = medications?.flatMap((medication) =>
    generateIntakePoints(medication, intervalStart, intervalEnd),
  );

  return points?.reduce((acc, point) => {
    const existing = acc?.find((item) => item?.label?.toLowerCase() === point?.label?.toLowerCase());

    if (existing) {
      existing?.data?.push({
        x: point?.x,
        y: point?.y,
        value: point?.value,
        unit: point?.unit,
        productName: point?.productName,
      });
    } else {
      acc?.push({
        label: point?.label,
        data: [
          {
            x: point?.x,
            y: point?.y,
            value: point?.value,
            unit: point?.unit,
            productName: point?.productName,
          },
        ],
        connectPoints: false,
        highlighted: true,
      });
    }

    return acc;
  }, []);
};

const getIntervalBounds = (dataSets) => {
  if (dataSets?.length === 0) {
    const now = new Date();
    return [subDays(now, 7), addDays(now, 7), 1];
  }

  const result = dataSets?.reduce(
    (acc, item) => {
      item?.data?.forEach((point) => {
        const xValue = parseDate(point?.x);
        const yValue = Number(point?.y);

        if (!xValue || !Number.isFinite(yValue)) {
          return;
        }

        if (acc?.minX === null) {
          acc.minX = new Date(xValue);
        }

        if (acc?.maxX === null) {
          acc.maxX = new Date(xValue);
        }

        if (xValue < acc?.minX) {
          acc.minX = new Date(xValue);
        } else if (xValue > acc?.maxX) {
          acc.maxX = new Date(xValue);
        }

        acc.maxY = Math.max(acc?.maxY, yValue);
      });

      return acc;
    },
    { minX: null, maxX: null, maxY: 0 },
  );

  if (!result?.minX || !result?.maxX) {
    const now = new Date();
    return [subDays(now, 7), addDays(now, 7), 1];
  }

  result.minX = new Date(result?.minX);
  result.minX?.setDate(result?.minX?.getDate() - 1);
  result.maxX = new Date(result?.maxX);
  result.maxX?.setDate(result?.maxX?.getDate() + 1);
  return [result?.minX, result?.maxX, Math.max(result?.maxY || 1, 1)];
};

export const getTranslatedLabel = (label, t) => {
  const labelParts = label?.split("+")?.map((part, i) =>
    i === 0 && label?.includes("plasma_concentration_test") ? t(part?.trim()) : part.trim());

  if (labelParts?.length === 3) {
    labelParts?.push(t("hours"));
  }

  return labelParts?.join(" ");
};

const DiaryValuesLineChart = ({
  header,
  values,
  maxYScale,
  patientId,
  onCloseDialog,
  showDialog,
}) => {
  const { t } = useTranslation();
  const context = useContext(InitContext);
  const chartRef = useRef(null);
  const lastInitialRangeKeyRef = useRef(null);
  const previousShowDialogRef = useRef(false);

  const { isDragging, dragStyle, onDragHandleMouseDown } = useDialogDrag(showDialog, {
    baseTransform: "translate(-50%, -50%)",
  });

  const [showIntakes, setShowIntakes] = useState(true);
  const [selectedDataSet, setSelectedDataSet] = useState(values?.[0]?.label);
  const [medicationVisibility, setMedicationVisibility] = useState({});
  const [viewRange, setViewRange] = useState(null);
  const { loading, error, clearError, getDbDrugs } = useMmcService();
  const [patDrugs, setPatDrugs] = useState([]);
  const resolvedPatientId = patientId || context?.patient?.id;

  useEffect(() => {
    if (!showDialog || !resolvedPatientId || !context?.token) {
      return;
    }

    let mounted = true;

    const fetchPrescriptionsWithIntakes = async () => {
      clearError();

      const drugs = await getDbDrugs(
        context?.token,
        resolvedPatientId,
        false,
        true,
      );

      if (mounted) {
        setPatDrugs(drugs || []);
      }
    };

    fetchPrescriptionsWithIntakes().then();

    return () => {
      mounted = false;
    };
  }, [showDialog, resolvedPatientId, context?.token]);

  const normalizedValues = useMemo(
    () => (values || [])
      .map((item) => ({
        ...item,
        displayLabel: getTranslatedLabel(item?.label, t),
        measurementKey: item?.measurementKey,
        measurementLabel: item?.measurementLabel,
        data: sortPointsChronologically((item?.data || []).map(normalizeChartPoint).filter(Boolean)),
      }))
      .filter((item) => item?.data?.length > 0),
    [t, values],
  );

    const measurementOptions = useMemo(() => {
    const groupedItems = normalizedValues.filter((item) => item?.measurementKey);

    if (!groupedItems.length) {
      return [];
    }

    const byMeasurement = new Map();

    groupedItems.forEach((item) => {
      if (!byMeasurement.has(item.measurementKey)) {
        byMeasurement.set(item.measurementKey, {
          value: item.measurementKey,
          label: item.measurementLabel || item.displayLabel,
        });
      }
    });

    return Array.from(byMeasurement.values());
    }, [normalizedValues]);

    const isMeasurementGroupedView = measurementOptions.length > 0;

    const parameterOptions = useMemo(() => {
    if (isMeasurementGroupedView) {
      return measurementOptions;
    }

    return normalizedValues?.map((item) => ({
      value: item?.label,
      label: item?.displayLabel,
    }));
    }, [isMeasurementGroupedView, measurementOptions, normalizedValues]);

    useEffect(() => {
    if (!normalizedValues?.length) {
      setSelectedDataSet(undefined);
      return;
    }

    if (isMeasurementGroupedView) {
      if (!selectedDataSet || !measurementOptions.some((option) => option?.value === selectedDataSet)) {
        setSelectedDataSet(measurementOptions?.[0]?.value);
      }

      return;
    }

    if (!selectedDataSet || !normalizedValues.some((item) => item?.label === selectedDataSet)) {
      setSelectedDataSet(normalizedValues[0]?.label);
    }
    }, [isMeasurementGroupedView, measurementOptions, normalizedValues, selectedDataSet]);

  const selectedValueSets = useMemo(
    () => {
      if (!selectedDataSet) {
        return normalizedValues;
      }

      if (isMeasurementGroupedView) {
        const selected = normalizedValues.filter((item) => item?.measurementKey === selectedDataSet);
        return selected.length > 0 ? selected : normalizedValues;
      }

      const selected = normalizedValues.filter((item) => item?.label === selectedDataSet);
      return selected.length > 0 ? selected : normalizedValues;
    },
    [isMeasurementGroupedView, normalizedValues, selectedDataSet],
  );

  const [intervalStart, intervalEnd, maxY] = useMemo(
    () => getIntervalBounds(selectedValueSets),
    [selectedValueSets],
  );

  const medicationsDataSets = useMemo(
    () => transformMedicationsToDataSets(patDrugs, intervalStart, intervalEnd)
      .map((item) => ({
        ...item,
        displayLabel: item?.label,
        data: sortPointsChronologically((item?.data || []).map(normalizeChartPoint).filter(Boolean)),
      }))
      .filter((item) => item?.data?.length > 0),
    [intervalEnd, intervalStart, patDrugs],
  );

  useEffect(() => {
    const nextVisibility = {};

    medicationsDataSets.forEach((item) => {
      nextVisibility[item.label] = true;
    });

    setMedicationVisibility((prev) => ({ ...nextVisibility, ...prev }));
  }, [medicationsDataSets]);

  const visibleValueSets = selectedValueSets;

  const visibleMedicationSets = useMemo(() => {
    if (!showIntakes) {
      return [];
    }

    return medicationsDataSets.filter((item) => medicationVisibility[item.label] !== false);
  }, [medicationVisibility, medicationsDataSets, showIntakes]);

  const allVisiblePoints = useMemo(
    () => [...visibleValueSets, ...visibleMedicationSets].flatMap((item) => item?.data || []),
    [visibleMedicationSets, visibleValueSets],
  );

  const initialRange = useMemo(() => {
    if (!allVisiblePoints?.length) {
      const now = new Date();
      return { min: subDays(now, 7), max: addDays(now, 7) };
    }

    const timestamps = allVisiblePoints.map((point) => point.x.getTime());
    return {
      min: subDays(new Date(Math.min(...timestamps)), 1),
      max: addDays(new Date(Math.max(...timestamps)), 1),
    };
  }, [allVisiblePoints]);

  const initialRangeKey = useMemo(
    () => `${initialRange?.min?.getTime?.() || ""}:${initialRange?.max?.getTime?.() || ""}`,
    [initialRange],
  );

  useEffect(() => {
    if (!showDialog) {
      previousShowDialogRef.current = false;
      return;
    }

    const dialogJustOpened = !previousShowDialogRef.current;
    const rangeChanged = lastInitialRangeKeyRef.current !== initialRangeKey;

    if (dialogJustOpened || rangeChanged || !viewRange) {
      setViewRange({
        min: new Date(initialRange.min),
        max: new Date(initialRange.max),
      });

      lastInitialRangeKeyRef.current = initialRangeKey;
    }

    previousShowDialogRef.current = true;
  }, [initialRange, initialRangeKey, showDialog, viewRange]);

  const errorMessage = error && (
    <ErrorMessage message={t("error_by_retrieving_prescribed_drugs")} />
  );

  const spinner = loading && <Spinner />;

  const getUnitLabel = useCallback(() => {
    const relevantDataSets = visibleValueSets?.length ? visibleValueSets : selectedValueSets;
    const units = relevantDataSets
      ?.flatMap((item) => item?.data?.map((dataPoint) => dataPoint?.unit))
      ?.filter(Boolean);

    const uniqueUnits = [...new Set(units)];

    if (uniqueUnits?.length === 1) {
      return uniqueUnits[0];
    }

    if (uniqueUnits?.length > 1) {
      return uniqueUnits.join(", ");
    }

    return "";
  }, [selectedValueSets, visibleValueSets]);

    // parameterOptions is now handled above with measurement-level support

  const shouldScrollMedicationLegend = medicationsDataSets.length > LEGEND_MAX_VISIBLE_ITEMS;

  const yAxisRange = useMemo(() => {
    const numericValues = visibleValueSets.flatMap((item) => item?.data?.map((point) => point?.y) || []);

    if (!numericValues.length) {
      return {
        min: 0,
        max: maxYScale || maxY || 1,
        step: undefined,
      };
    }

    const dataMin = Math.min(...numericValues);
    const dataMax = Math.max(...numericValues);
    const firstSet = visibleValueSets?.[0] || selectedValueSets?.[0];
    const sourceKey = normalizeSourceKey(firstSet?.sourceKey);
    const isWeight = sourceKey === Consts.WEIGHT;
    const isHeight = sourceKey === Consts.HEIGHT;

    if (sourceKey === "systole") {
      return {
        min: 30,
        max: 250,
        step: undefined,
      };
    }

    if (sourceKey === "diastole") {
      return {
        min: 30,
        max: 150,
        step: undefined,
      };
    }

    if (sourceKey === "pulse") {
      return {
        min: 30,
        max: 160,
        step: undefined,
      };
    }

    if (sourceKey === "body_temperature") {
      return {
        min: 33,
        max: 43,
        step: 1,
      };
    }

    if (isWeight || isHeight) {
      const globalMin = isWeight ? Consts.WEIGHT_MIN : Consts.HEIGHT_MIN;
      const globalMax = isWeight ? Consts.WEIGHT_MAX : Consts.HEIGHT_MAX;
      const fixedStep = 5;
      let rangeMin = Math.max(globalMin, Math.floor(dataMin - 30));
      let rangeMax = Math.min(globalMax, Math.ceil(dataMax + 30));

      if (rangeMax - rangeMin < 20) {
        const center = (rangeMin + rangeMax) / 2;
        rangeMin = Math.max(globalMin, Math.floor(center - 10));
        rangeMax = Math.min(globalMax, Math.ceil(center + 10));
      }

      // Align measurement ranges to 5-unit ticks for stable/clean axis labels.
      rangeMin = Math.max(globalMin, Math.floor(rangeMin / fixedStep) * fixedStep);
      rangeMax = Math.min(globalMax, Math.ceil(rangeMax / fixedStep) * fixedStep);

      return {
        min: rangeMin,
        max: Math.max(rangeMax, rangeMin + 1),
        step: fixedStep,
      };
    }

    const fallbackMax = Number.isFinite(dataMax) && dataMax > 1
      ? Math.ceil(dataMax * 1.15)
      : 1;

    return {
      min: 0,
      max: maxYScale || fallbackMax,
      step: Number.isInteger((maxYScale || fallbackMax)) && (maxYScale || fallbackMax) <= 30 ? 1 : undefined,
    };
  }, [maxY, maxYScale, selectedValueSets, visibleValueSets]);

  const chartData = useMemo(() => ({
    datasets: [
      ...visibleValueSets.map((dataSet) => {
        const color = stableColorForKey(dataSet?.label, VALUE_PALETTE);

        return {
          label: dataSet?.displayLabel,
          data: dataSet?.data,
          datasetKey: dataSet?.label,
          datasetType: "value",
          borderColor: color,
          backgroundColor: color,
          pointBackgroundColor: color,
          pointBorderColor: "#ffffff",
          pointBorderWidth: 1.5,
          borderWidth: dataSet?.connectPoints ? 3 : 0,
          pointRadius: dataSet?.connectPoints ? 4 : 5,
          pointHoverRadius: 6,
          pointHitRadius: 14,
          pointStyle: "circle",
          tension: 0.22,
          fill: false,
          spanGaps: false,
        };
      }),
      ...visibleMedicationSets.map((dataSet) => {
        const color = stableColorForKey(dataSet?.label, MEDICATION_PALETTE);

        return {
          label: dataSet?.displayLabel,
          data: dataSet?.data?.map((point) => ({
            ...point,
            y: yAxisRange.min,
          })),
          datasetKey: dataSet?.label,
          datasetType: "medication",
          borderColor: color,
          backgroundColor: color,
          pointBackgroundColor: color,
          pointBorderColor: "#ffffff",
          pointBorderWidth: 1.5,
          borderWidth: 0,
          showLine: false,
          clip: false,
          pointRadius: 5,
          pointHoverRadius: 7,
          pointHitRadius: 14,
          pointStyle: "rectRounded",
        };
      }),
    ],
  }), [visibleMedicationSets, visibleValueSets, yAxisRange.min]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: {
        mode: "nearest",
        intersect: true,
      },
      scales: {
        x: {
          type: "time",
          min: viewRange?.min,
          max: viewRange?.max,
          time: {
            unit: "day",
            tooltipFormat: "dd/MM/yyyy",
            displayFormats: {
              day: "dd/MM/yyyy",
            },
          },
          ticks: {
            color: "#1565a0",
            callback: (value) => {
              const tickDate = new Date(value);
              return isValid(tickDate) ? format(tickDate, "dd/MM/yyyy") : "";
            },
          },
          grid: {
            color: "#e3f2fd",
          },
          title: {
            display: true,
            text: t("date", { defaultValue: "Date" }),
            color: "#1565a0",
            font: {
              weight: 600,
            },
          },
        },
        y: {
          beginAtZero: false,
          min: yAxisRange.min,
          max: yAxisRange.max,
          ticks: {
            stepSize: yAxisRange.step,
            color: "#1565a0",
          },
          grid: {
            color: "#e3f2fd",
          },
          title: {
            display: true,
            text: getUnitLabel(),
            color: "#1565a0",
            font: {
              weight: 600,
            },
          },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            title: (items) => {
              const point = items?.[0]?.raw;
              return formatTooltipDate(point?.x);
            },
            label: (context) => {
              const pointData = context?.raw;
              const value = pointData?.value ?? context?.parsed?.y;
              const unitSuffix = pointData?.unit ? ` ${pointData.unit}` : "";

              if (context?.dataset?.datasetType === "medication") {
                return `${t("medication", { defaultValue: "Medicine" })}: ${pointData?.productName || context?.dataset?.label} — ${value}${unitSuffix}`;
              }

              const extras = (pointData?.extraValues || [])
                .filter((extra) => extra && Number.isFinite(Number(extra?.value)))
                .map((extra) => `${extra?.label || ""}: ${extra?.value}${extra?.unit ? ` ${extra.unit}` : ""}`)
                .join(", ");

              const endDateText = pointData?.endDate
                ? `, ${t("end_date", { defaultValue: "End date" })}: ${pointData.endDate}`
                : "";

              const extraText = extras ? `, ${extras}` : "";

              return `${context?.dataset?.label}: ${value}${unitSuffix}${endDateText}${extraText}`;
            },
          },
        },
        zoom: {
          pan: {
            enabled: true,
            mode: "x",
            onPanComplete: ({ chart }) => {
              const xScale = chart?.scales?.x;
              if (xScale) {
                setViewRange({ min: new Date(xScale.min), max: new Date(xScale.max) });
              }
            },
          },
          zoom: {
            wheel: {
              enabled: true,
            },
            pinch: {
              enabled: true,
            },
            mode: "x",
            onZoomComplete: ({ chart }) => {
              const xScale = chart?.scales?.x;

              if (xScale) {
                setViewRange({ min: new Date(xScale.min), max: new Date(xScale.max) });
              }
            },
          },
        },
      },
    }),
    [getUnitLabel, t, viewRange, yAxisRange.max, yAxisRange.min],
  );

  const toggleMedicationVisibility = useCallback((key) => {
    setMedicationVisibility((prev) => ({ ...prev, [key]: !(prev[key] !== false) }));
  }, []);

  const handleToggleShowIntakes = useCallback(() => {
    setShowIntakes((prev) => {
      const next = !prev;

      if (next) {
        const defaults = {};

        medicationsDataSets.forEach((item) => {
          defaults[item.label] = true;
        });

        setMedicationVisibility(defaults);
      }

      return next;
    });
  }, [medicationsDataSets]);

  const shiftWindow = useCallback((direction) => {
    if (!viewRange) {
      return;
    }

    const spanMs = viewRange.max.getTime() - viewRange.min.getTime();
    const offset = spanMs * direction;

    setViewRange({
      min: new Date(viewRange.min.getTime() + offset),
      max: new Date(viewRange.max.getTime() + offset),
    });
  }, [viewRange]);

  const zoomByFactor = useCallback((factor) => {
    chartRef.current?.zoom?.(factor);
  }, []);

  const resetZoom = useCallback(() => {
    chartRef.current?.resetZoom?.();
    setViewRange(initialRange);
  }, [initialRange]);

  if (!showDialog) {
    return null;
  }

  return (
    <div
      className="w3-modal"
      style={{ display: Consts.BLOCK, backgroundColor: "rgba(10, 30, 60, 0.35)" }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onCloseDialog();
        }
      }}
    >
      <div
        className={`w3-card-4 w3-animate-zoom timeline-chart-modal dialog-draggable ${isDragging ? "dialog-draggable--dragging" : ""}`}
        style={dragStyle}
      >
        <header className="dialog-drag-header" onMouseDown={onDragHandleMouseDown}>
          <strong className="dialog-drag-header__title">{header}</strong>
          <button
            type="button"
            className="dialog-drag-close"
            onClick={onCloseDialog}
            aria-label={t("close", { defaultValue: "Close" })}
            title={t("close", { defaultValue: "Close" })}
          >
            ×
          </button>
        </header>
        {errorMessage}
        {spinner}
        <div className="timeline-chart-controls-panel">
          <div className="timeline-chart-toolbar">
            <button type="button" className="w3-button w3-round timeline-chart-toolbar-button" onClick={() => shiftWindow(-1)}>
              {"<"} {t("previous", { defaultValue: "Previous" })}
            </button>
            <button type="button" className="w3-button w3-round timeline-chart-toolbar-button" onClick={() => shiftWindow(1)}>
              {t("next", { defaultValue: "Next" })} {">"}
            </button>
            <button type="button" className="w3-button w3-round timeline-chart-toolbar-button" onClick={() => zoomByFactor(1.25)}>
              {t("zoom_in", { defaultValue: "Zoom +" })}
            </button>
            <button type="button" className="w3-button w3-round timeline-chart-toolbar-button" onClick={() => zoomByFactor(0.8)}>
              {t("zoom_out", { defaultValue: "Zoom -" })}
            </button>
            <button type="button" className="w3-button w3-round timeline-chart-toolbar-button" onClick={resetZoom}>
              {t("reset", { defaultValue: "Reset" })}
            </button>

            {parameterOptions.length > 1 && (
              <div className="timeline-chart-select-wrap" data-no-drag="true">
                <label htmlFor="dataSetSelect" className="timeline-chart-select-label">
                  {t("select_dataset")}:
                </label>
                <Select
                  inputId="dataSetSelect"
                  options={parameterOptions}
                  value={parameterOptions?.find((option) => option?.value === selectedDataSet) || null}
                  onChange={(selectedOption) => setSelectedDataSet(selectedOption?.value)}
                  placeholder={t("select")}
                  className="symptom-compact-select timeline-chart-select"
                  classNamePrefix="meds-sort-select"
                  isSearchable={false}
                  menuPosition="fixed"
                  menuShouldScrollIntoView={false}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 10080 }),
                    menu: (base) => ({ ...base, zIndex: 10080 }),
                  }}
                />
              </div>
            )}

            {medicationsDataSets?.length > 0 ? (
              <label className="timeline-chart-checkbox">
                <input
                  type="checkbox"
                  checked={showIntakes}
                  onChange={handleToggleShowIntakes}
                />
                {t("show_medication_intakes")}
              </label>
            ) : (
              <span className="timeline-chart-empty-hint">{t("no_medicines_to_show")}</span>
            )}
          </div>

          <div className="timeline-chart-legend-grid">
            {showIntakes && medicationsDataSets.length > 0 && (
              <div className="w3-card timeline-chart-legend-card">
                <strong>{t("medications", { defaultValue: "Medicines" })}</strong>
                <div
                  className="timeline-chart-legend-list"
                  style={{
                    maxHeight: shouldScrollMedicationLegend ? `${LEGEND_SCROLL_HEIGHT_PX}px` : "none",
                    overflowY: shouldScrollMedicationLegend ? "auto" : "visible",
                  }}
                >
                  {medicationsDataSets.map((item) => (
                    <label key={item.label} className="timeline-chart-legend-item">
                      <input
                        type="checkbox"
                        checked={medicationVisibility[item.label] !== false}
                        onChange={() => toggleMedicationVisibility(item.label)}
                      />
                      <span
                        className="timeline-chart-legend-dot"
                        style={{ background: stableColorForKey(item.label, MEDICATION_PALETTE) }}
                      />
                      <span className="symptoms-medication-label-ellipsis">{item.displayLabel}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="timeline-chart-canvas-wrap">
          <Line ref={chartRef} data={chartData} options={chartOptions} />
        </div>
      </div>
    </div>
  );
};

export default DiaryValuesLineChart;
